import { deleteRSOApi, useRSO } from "@/api/services/admin/rso";
import { Button } from "@/components/elements/Button";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { TopPage } from "@/components/elements/TopPage";
import { PartnerStatusTag } from "@/features/rso/components/PartnerStatusTag";
import { PartnerSummary } from "@/features/rso/components/PartnerSummary";
import { Stack, styled, Typography } from "@mui/material";
import { FC, useEffect } from "react";
import { ERSOContractStatus } from "@/types/enum";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { PartnerDescription } from "@/features/rso/components/PartnerDescription";
import { useParams, useNavigate } from "react-router-dom";
import { PartnerFooterContainer } from "@/features/rso/components/PartnerFooterContainer";
import { PartnerUserTable } from "@/features/rso/components/PartnerUserTable";
import { AppRoutes } from "@/routes/config";
import { isNumber, formatRSOId } from "@/utils/number";
import { formatDateTime } from "@/utils/datetime";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { showError } from "@/utils/error";
import { useQueryClient } from "@tanstack/react-query";
import { IconTrash } from "@/assets/icons/IconTrash";
import { Tag } from "@/components/elements/Tag";
import { black } from "@/theme/colors";

const DELETABLE_CONTRACT_STATUSES: string[] = [ERSOContractStatus.TRIAL_CANCELLED, ERSOContractStatus.CANCELED_CONTRACT];

export const PartnerDetailPage: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDelete = useDialog();
  const { data: partnerResult, isLoading, isError } = useRSO();
  const partnerData = partnerResult?.data;
  const isDeleted = !!partnerData?.deletedAt;
  const canDelete = partnerData ? DELETABLE_CONTRACT_STATUSES.includes(partnerData.contractStatus) : false;

  const handleDelete = async () => {
    try {
      if (!id) return;
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteRSOApi(id);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.duringContractList);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  useEffect(() => {
    if (!id || !isNumber(id)) {
      navigate(AppRoutes.duringContractList, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id && isNumber(id) && !isLoading && (isError || !partnerData)) {
      navigate(AppRoutes.duringContractList, { replace: true });
    }
  }, [id, isLoading, isError, partnerData, navigate]);

  if (isLoading || !partnerData) return <LoadingPage />;

  return (
    <PartnerDetailWrapper gap={3}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="flex-end" gap={2}>
            {partnerData.name}
            <PartnerStatusTag status={partnerData.contractStatus} variant="normal" />
          </Typography>
        }
        description={
          <Typography variant="body14">
            組織ID：{formatRSOId(partnerData.id)}
            <span style={{ marginLeft: "16px" }}>最終ログイン日時：{formatDateTime(partnerData.lastLoginTime)}</span>
          </Typography>
        }
        rightContent={
          isDeleted ? (
            <Tag enablePoint variant="bold" title="削除済み" color={black[95]} pointColor={black[70]} />
          ) : (
            canDelete && (
              <Button
                size="sm"
                variant={"danger"}
                startIcon={<IconTrash />}
                sx={{
                  width: "88px",
                }}
                onClick={handleDelete}
              >
                削除
              </Button>
            )
          )
        }
        enablePageBack={false}
      />
      <MenuNavigate
        contents={[
          { title: "基本情報", content: <PartnerSummary /> },
          { title: "その他情報", content: <PartnerDescription /> },
          { title: "顧客ユーザーの一覧", content: <PartnerUserTable name={partnerData.name} isDeleted={isDeleted} /> },
        ]}
        contentWidth="900px"
        contentAffix={<PartnerFooterContainer partnerId={id} hideEdit={isDeleted} />}
      />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message="この組織を削除してもよろしいですか？"
      />
    </PartnerDetailWrapper>
  );
};

const PartnerDetailWrapper = styled(Stack)`
  margin: 0 auto;
`;
